import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that manages Klaviyo back in stock notifications.
 *
 * @typedef {object} KlaviyoBackInStockRefs
 * @property {HTMLFormElement} form - The form element.
 * @property {HTMLElement | undefined} successMessage - The success message element.
 * @property {HTMLElement | undefined} errorMessage - The error message element.
 * @property {HTMLInputElement | undefined} variantInput - The hidden input for variant ID.
 *
 * @extends Component<KlaviyoBackInStockRefs>
 */
class KlaviyoBackInStockComponent extends Component {
  requiredRefs = ['form'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #messageTimeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();

    if (this.#messageTimeout) {
      clearTimeout(this.#messageTimeout);
    }
  }

  /**
   * Removes success and error messages.
   */
  #removeMessages() {
    if (this.#messageTimeout) {
      clearTimeout(this.#messageTimeout);
    }

    this.refs.successMessage?.classList.add('hidden');
    this.refs.errorMessage?.classList.add('hidden');
  }

  /**
   * Handles the form submit event.
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    event.preventDefault();

    this.#removeMessages();

    const formData = new FormData(this.refs.form);

    const backInStockSubscription = async () => {
      return fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${formData.get('a')}`, {
        method: 'POST',
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: formData.get('email'),
                  },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  id: `$shopify:::$default:::${formData.get('variant')}`,
                  type: 'catalog-variant',
                },
              },
            },
          },
        }),
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Revision: '2024-07-15',
        },
      });
    };

    const newsletterSubscription = async () => {
      if (formData.get('subscribe_for_newsletter') === 'on') {
        return fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${formData.get('a')}`, {
          method: 'POST',
          body: JSON.stringify({
            data: {
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: formData.get('email'),
                    },
                  },
                },
              },
              relationships: {
                list: {
                  data: {
                    id: formData.get('g'),
                    type: 'list',
                  },
                },
              },
              type: 'subscription',
            },
          }),
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Revision: '2024-07-15',
          },
        });
      }

      // Resolve immediately if newsletter subscription is not needed
      return Promise.resolve({ status: 200 });
    };

    try {
      // Execute both fetch calls concurrently
      const results = await Promise.all([backInStockSubscription(), newsletterSubscription()]);

      // Check if both fetch calls were successful
      if (results.every((res) => res.status === 200 || res.status === 202)) {
        this.refs.successMessage?.classList.remove('hidden');
      } else {
        this.refs.errorMessage?.classList.remove('hidden');
      }

      // Auto-hide messages after 10 seconds
      this.#messageTimeout = setTimeout(() => {
        this.#removeMessages();
      }, 10000);
    } catch (error) {
      console.error(error);
      this.refs.errorMessage?.classList.remove('hidden');

      // Auto-hide error message after 10 seconds
      this.#messageTimeout = setTimeout(() => {
        this.#removeMessages();
      }, 10000);
    }
  }

  /**
   * Handles variant update events.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #onVariantUpdate = (event) => {
    // Check if this is the correct product
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const variantInput = this.refs.variantInput;

    if (!variantInput) return;

    // Update the variant ID in the hidden input
    variantInput.value = event.detail.resource?.id ?? '';

    // Show or hide the form based on variant availability
    if (event.detail.resource == null || event.detail.resource.available == false) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }
  };
}

if (!customElements.get('klaviyo-back-in-stock-component')) {
  customElements.define('klaviyo-back-in-stock-component', KlaviyoBackInStockComponent);
}
